import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "grid md:grid-cols-5 gap-2 mb-4 lg:mb-12 items-center" }
const _hoisted_2 = { class: "md:col-span-2" }
const _hoisted_3 = { class: "md:col-span-2" }
const _hoisted_4 = { class: "sm:basis-1/6 sm:pr-10 pb-2" }
const _hoisted_5 = { class: "sm:basis-5/6" }
const _hoisted_6 = { class: "grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = {
  key: 0,
  class: "w-full text-xs"
}
const _hoisted_9 = { class: "font-semibold text-gray-500 dark:text-white text-sm mb-2" }
const _hoisted_10 = { class: "flex items-center justify-between" }
const _hoisted_11 = {
  key: 1,
  class: "text-gray-700 dark:text-gray-300"
}
const _hoisted_12 = {
  key: 0,
  class: "absolute right-2 top-2"
}
const _hoisted_13 = { class: "text-gray-700 dark:text-gray-300" }
const _hoisted_14 = {
  key: 0,
  class: "font-bold"
}
const _hoisted_15 = {
  key: 1,
  class: "flex flex-col items-center justify-center"
}
const _hoisted_16 = ["src", "alt"]
const _hoisted_17 = ["src", "alt"]
const _hoisted_18 = { class: "font-semibold text-gray-500 dark:text-white text-sm text-center" }

import { pb } from "@/pocketbase";
import { getTechnologyCategoryLabelForTechnologyType, getTechnologyTypeLabel, Product, ProductTechnology, Technology, technologyTypesByCategory } from "@/model";
import { computed, Ref, ref } from "vue";

import Input from "@/components/Input.vue";
import Listbox from "@/components/Listbox.vue";
import Title from "@/components/Title.vue";
import Heading from "@/components/Heading.vue";
import Heading2 from "@/components/Heading2.vue";
import Divider from "@/components/Divider.vue";
import Content from "@/components/Content.vue";

type TechnologyCategoryUsage = {
  categoryLabel: string,
  types: TechnologyTypeUsage[],
};

type TechnologyTypeUsage = {
  typeLabel: string,
  technologies: TechnologyUsage[],
};

type TechnologyUsage = {
  technology: Technology,
  productTechnologies: ProductTechnology[],
};


export default /*@__PURE__*/_defineComponent({
  __name: 'TechLandscapeView',
  async setup(__props) {

let __temp: any, __restore: any

const searchTerm = ref('');

const products: Ref<Product[]> = ref([]);
const productTechnologies: Ref<ProductTechnology[]> = ref([]);

async function loadData() {
  products.value = await pb.collection('products').getFullList<Product>(null, {
    expand: 'businessUnit',
    sort: 'name',
    '$autoCancel': false,
  });

  productTechnologies.value = await pb.collection('product_technologies').getFullList<ProductTechnology>(null, {
    expand: 'product,technology,product.businessUnit',
    sort: 'technology.name',
    '$autoCancel': false,
  });
}

;(
  ([__temp,__restore] = _withAsyncContext(() => loadData())),
  await __temp,
  __restore()
);

const businessUnitOptions = [{ key: '', label: 'All' }].concat(
  Array.from(new Map(products.value.map(product => [
    product.expand!.businessUnit!.id,
    {
      key: product.expand!.businessUnit!.id,
      label: product.expand!.businessUnit!.name
    }
  ])).values())
  .sort((a, b) => a.label.localeCompare(b.label))
);
const selectedBusinessUnit = ref(businessUnitOptions[0]);

const categories = computed(() => {
  let categoryUsages: TechnologyCategoryUsage[] = [];

  for (const productTechnology of productTechnologies.value) {
    if (searchTerm.value.length > 0 && !productTechnology.expand!.technology!.name.match(new RegExp(searchTerm.value, 'gi'))) {
      continue;
    }

    if (selectedBusinessUnit.value.key.length > 0 && selectedBusinessUnit.value.key !== productTechnology.expand!.product!.businessUnit) {
      continue;
    }

    const technology = productTechnology.expand!.technology!;
    const technologyTypeLabel = getTechnologyTypeLabel(technology.type);
    const technologyCategoryLabel = getTechnologyCategoryLabelForTechnologyType(technology.type);

    let categoryUsage = categoryUsages.find(u => u.categoryLabel === technologyCategoryLabel);
    if (!categoryUsage) {
      categoryUsage = {
        categoryLabel: technologyCategoryLabel,
        types: [],
      };
      categoryUsages.push(categoryUsage);

      // sort based on technologyTypesByCategory
      categoryUsages = categoryUsages.sort((a, b) => {
        const a1 = technologyTypesByCategory.indexOf(technologyTypesByCategory.find(({ category }) => category === a.categoryLabel));
        const b1 = technologyTypesByCategory.indexOf(technologyTypesByCategory.find(({ category }) => category === b.categoryLabel));

        return a1 - b1;
      })
    }

    let typeUsage = categoryUsage.types.find(u => u.typeLabel === technologyTypeLabel);
    if (!typeUsage) {
      typeUsage = {
        typeLabel: technologyTypeLabel,
        technologies: [],
      }
      categoryUsage.types.push(typeUsage);

      // sort based on technologyTypesByCategory
      const sortedTypes = technologyTypesByCategory.find(({ category }) => category === technologyCategoryLabel)?.types;
      categoryUsage.types = categoryUsage.types.sort((a, b) => {
        return sortedTypes.indexOf(a.typeLabel) - sortedTypes.indexOf(b.typeLabel);
      })
    }

    let technologyUsage = typeUsage.technologies.find(t => t.technology.id === technology.id);
    if (!technologyUsage) {
      technologyUsage = {
        technology,
        productTechnologies: [],
      }
      typeUsage.technologies = typeUsage.technologies
      .concat(technologyUsage)
      .sort((a, b) => a.technology.name.localeCompare(b.technology.name));
    }

    technologyUsage.productTechnologies = technologyUsage.productTechnologies
    .concat(productTechnology)
    .sort((a, b) => a.expand!.product!.name.localeCompare(b.expand!.product!.name));
  }

  return categoryUsages;
});

function getLogoUrl(technology: Technology): string {
  return pb.files.getUrl(technology, technology.logo);
}

const selectedTechnology: Ref<Technology | null> = ref(null);

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Title, { subtitle: "Technologies used by TSG business units" }, {
      default: _withCtx(() => _cache[2] || (_cache[2] = [
        _createTextVNode("Tech Landscape")
      ])),
      _: 1,
      __: [2]
    }),
    _createVNode(Divider),
    _createVNode(Content, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(Input, {
              modelValue: searchTerm.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
              label: "Search term",
              placeholder: "Search technology ...",
              marginless: ""
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(Listbox, {
              label: "Business Unit",
              optional: "",
              modelValue: selectedBusinessUnit.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedBusinessUnit).value = $event)),
              options: _unref(businessUnitOptions),
              marginless: ""
            }, null, 8, ["modelValue", "options"])
          ])
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, ({categoryLabel, types}) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            _createVNode(Heading, null, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(categoryLabel), 1)
              ]),
              _: 2
            }, 1024),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(types, ({typeLabel, technologies}, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(['flex flex-col sm:flex-row', idx > 0 ? 'border-t border-gray-200 dark:border-gray-700 pt-4' : ''])
              }, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(Heading2, null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(typeLabel), 1)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(technologies, (usage) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: _normalizeClass([
                 selectedTechnology.value?.id === usage.technology.id ? 'col-span-3' : 'items-center',
                 'cursor-pointer p-2 sm:p-4 h-40 sm:h-48 rounded-lg border border-gray-300 dark:border-gray-600 flex justify-center',
               ]),
                        onClick: ($event: any) => (selectedTechnology.value = selectedTechnology.value?.id === usage.technology.id ? null : usage.technology)
                      }, [
                        (selectedTechnology.value?.id === usage.technology.id)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                              _createElementVNode("h3", _hoisted_9, _toDisplayString(usage.technology.name), 1),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(usage.productTechnologies, (productTechnology) => {
                                return (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                  (selectedBusinessUnit.value.key.length > 0)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createElementVNode("div", null, [
                                          (productTechnology.comment.length > 0)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createTextVNode(_toDisplayString(productTechnology.comment), 1)
                                              ], 64))
                                            : (_openBlock(), _createElementBlock("span", _hoisted_11, "no comment"))
                                        ]),
                                        (productTechnology.version.length > 0)
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, " version " + _toDisplayString(productTechnology.version), 1))
                                          : _createCommentVNode("", true)
                                      ], 64))
                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createElementVNode("div", null, [
                                          _createElementVNode("span", _hoisted_13, _toDisplayString(productTechnology.expand?.product?.name), 1),
                                          _createTextVNode(" by " + _toDisplayString(productTechnology.expand?.product?.expand?.businessUnit?.name), 1)
                                        ]),
                                        _createElementVNode("div", null, [
                                          (productTechnology.version.length > 0)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_14, "v " + _toDisplayString(productTechnology.version), 1))
                                            : _createCommentVNode("", true)
                                        ])
                                      ], 64))
                                ]))
                              }), 256)),
                              _cache[4] || (_cache[4] = _createElementVNode("hr", { class: "my-2 border-gray-300 dark:border-gray-700" }, null, -1)),
                              _createVNode(_component_router_link, {
                                class: "default-anchor",
                                to: { name: 'technology', params: { id: usage.technology.id }}
                              }, {
                                default: _withCtx(() => _cache[3] || (_cache[3] = [
                                  _createTextVNode(" Details ")
                                ])),
                                _: 2,
                                __: [3]
                              }, 1032, ["to"])
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                              (usage.technology.simpleicon)
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    src: `https://cdn.simpleicons.org/${usage.technology.simpleicon}`,
                                    alt: `Logo of ${usage.technology.name}`,
                                    class: "mb-2 w-12 h-12 object-contain"
                                  }, null, 8, _hoisted_16))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    (usage.technology.logo)
                                      ? (_openBlock(), _createElementBlock("img", {
                                          key: 0,
                                          src: getLogoUrl(usage.technology),
                                          alt: `Logo of ${usage.technology.name}`,
                                          class: "mb-1 w-10 h-10 object-contain"
                                        }, null, 8, _hoisted_17))
                                      : _createCommentVNode("", true)
                                  ], 64)),
                              _createElementVNode("h3", _hoisted_18, _toDisplayString(usage.technology.name), 1)
                            ]))
                      ], 10, _hoisted_7))
                    }), 256))
                  ])
                ])
              ], 2))
            }), 256))
          ], 64))
        }), 256))
      ]),
      _: 1
    })
  ], 64))
}
}

})